import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputMaskModule } from 'primeng/inputmask';
import { DropdownModule } from 'primeng/dropdown';
import { MultiSelectModule } from 'primeng/multiselect';
import { TooltipModule } from 'primeng/tooltip';

import {
  Colaborador,
  ColaboradorAtualizacaoPayload,
  ColaboradorPayload,
  EstadoCivil,
  Genero
} from 'src/app/pages/pessoas/models/colaborador.model';
import { ColaboradoresApiService } from 'src/app/core/services/colaboradores-api.service';
import { CargosApiService } from 'src/app/pages/empresa/pages/cargos/services/cargos-api.service';
import { DepartamentosApiService } from 'src/app/pages/empresa/pages/departamentos/services/departamentos-api.service';
import { HierarquiaApiService } from 'src/app/core/services/hierarquia-api.service';
import { CriarUsuarioPortalRequest, HierarquiaEntidadeResposta } from 'src/app/pages/empresa/pages/organograma/models/hierarquia.model';
import { TimesApiService } from 'src/app/pages/empresa/pages/times/services/times-api.service';
import { TimeResumo } from 'src/app/pages/empresa/pages/times/models/time.model';
import { AuthImageDirective } from 'src/app/shared/directives/auth-image.directive';
import { extractManagerErrorMessage } from 'src/app/core/utils/error.utils';
import {
  extractTimesJaAtribuidosConflitos,
  TimeConflito
} from 'src/app/core/utils/transferencia-times.utils';
import { TransferenciaTimesConfirmDialogComponent } from 'src/app/shared/transferencia-times-confirm-dialog/transferencia-times-confirm-dialog.component';
// BUG-015 (regressivo-pos-migracao 2026-07-29): consolidacao de opcoes canonicas.
// Antes o SHARED tinha arrays locais (Genero/EstadoCivil) que divergiam levemente
// dos ja centralizados em shared/constants (usados por aba-hierarquia apos BUG-012;
// modal-criar-usuario-portal foi consumer historico ate AUDIT-06 removê-lo por
// dead code). Reusar a fonte unica evita mais drift.
import { ESTADO_CIVIL_OPTIONS, GENERO_OPTIONS, OpcaoEnum } from 'src/app/shared/constants/enums.constants';

@Component({
  selector: 'app-colaborador-form-modal',
  templateUrl: './colaborador-form-modal.component.html',
  styleUrls: ['./colaborador-form-modal.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    InputTextModule,
    InputMaskModule,
    DropdownModule,
    MultiSelectModule,
    ButtonModule,
    CalendarModule,
    TooltipModule,
    AuthImageDirective,
    TransferenciaTimesConfirmDialogComponent
  ]
})
export class ColaboradorFormModalComponent implements OnChanges, OnDestroy {
  @Input() visible = false;
  @Input() colaborador: Colaborador | null = null;
  @Input() showSuccessToast = true;
  @Input() loadingData = false;
  @Input() perfil: 'GESTOR' | 'COLABORADOR' = 'COLABORADOR';

  // PR-4: prefill contextual do hover no organograma.
  // Todos opcionais — se ausentes, comportamento original é preservado (fluxo canônico limpo).
  @Input() prefillDepartamentoId: number | null = null;
  @Input() prefillTimeId: number | null = null;
  @Input() prefillGestorPaiId: number | null = null;

  @Output() close = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  loading = false;
  hoje: Date = new Date();

  // BUG-017: estado do modal de confirmacao de transferencia de times.
  // `payloadEmEspera` guarda o payload original ate o admin confirmar (ou
  // cancelar). Se confirmar, re-submetemos o MESMO payload + a flag
  // `forcarTransferencia: true`. Se cancelar, apenas descartamos.
  transferenciaDialogVisivel = false;
  transferenciaLoading = false;
  conflitosTransferencia: TimeConflito[] = [];
  private payloadGestorEmEspera: CriarUsuarioPortalRequest | null = null;
  dataNascimentoCalendar: Date | null = null;
  dataContratacaoCalendar: Date | null = null;
  cargosCarregando = false;
  departamentosCarregando = false;
  gestoresCarregando = false;
  timesCarregando = false;

  // Stepper (creation mode only)
  currentStep = 1;
  readonly totalSteps = 3;

  // Quick Create — Departamento
  quickCreateDepartamento = false;
  novoDepartamentoNome = '';
  criandoDepartamento = false;

  // Quick Create — Cargo
  quickCreateCargo = false;
  novoCargoNome = '';
  criandoCargo = false;

  // Quick Create — Time
  quickCreateTime = false;
  novoTimeNome = '';
  criandoTime = false;

  cargosOptions: { label: string; value: string }[] = [];
  departamentosOptions: { label: string; value: number }[] = [];
  gestorOptions: HierarquiaEntidadeResposta[] = [];
  timesOptions: TimeResumo[] = [];
  // BUG-015 (regressivo-pos-migracao 2026-07-29): campo "Chefe direto" removido
  // (P6 Task 5 revertido em conjunto com BUG-012 / DEBITO-01). Vinculo GESTOR→GESTOR
  // e definido pelo organograma (drag-drop / edicao) apos criacao. Se voltar como
  // requisito, reabrir com nova UX empresa-wide.

  private lookupsLoaded = false;
  private isInitialLoad = false;
  private destroy$ = new Subject<void>();
  private avatarFile: File | null = null;
  avatarPreviewUrl: string | null = null;

  private readonly allowedAvatarMimeTypes = ['image/jpeg', 'image/png'];
  private readonly allowedAvatarExtensions = ['.jpg', '.jpeg', '.png'];
  private readonly maxAvatarSizeBytes = 2 * 1024 * 1024; // 2MB

  // BUG-015: reusa constantes canonicas (shared/constants/enums.constants) para
  // evitar drift entre este SHARED e demais modais (form embutido de aba-hierarquia).
  // Se labels precisarem de update, mexer so no arquivo canonico.
  // Nota historica: modal-criar-usuario-portal tambem consumia estas constantes,
  // porem foi removido em AUDIT-06 (2026-07-29) por ser dead code.
  readonly estadoCivilOptions: OpcaoEnum<EstadoCivil>[] = ESTADO_CIVIL_OPTIONS;
  readonly generoOptions: OpcaoEnum<Genero>[] = GENERO_OPTIONS;

  readonly jornadaOptions: { label: string; value: number }[] = [
    { label: '4h',  value: 4  },
    { label: '6h',  value: 6  },
    { label: '8h',  value: 8  },
    { label: '10h', value: 10 },
    { label: '12h', value: 12 },
    { label: '14h', value: 14 },
    { label: '16h', value: 16 }
  ];

  form = this.fb.group({
    identificador: this.fb.nonNullable.control('', [Validators.required]),
    nome: this.fb.nonNullable.control('', [Validators.required]),
    sobrenome: this.fb.nonNullable.control('', [Validators.required]),
    email: this.fb.nonNullable.control('', [Validators.required, Validators.email]),
    telefone: this.fb.control(''),
    cargo: this.fb.nonNullable.control('', [Validators.required]),
    gestorId: this.fb.control<number | null>({ value: null, disabled: true }, [Validators.required]),
    departamentoId: this.fb.nonNullable.control<number | null>(null, [Validators.required]),
    emailPessoal: this.fb.control(''),
    estadoCivil: this.fb.control<EstadoCivil | null>(null),
    dataContratacao: this.fb.control<string | null>(null),
    genero: this.fb.control<Genero | null>(null),
    dataNascimento: this.fb.control<string | null>(null),
    timesIds: this.fb.control<number[] | null>(null, [Validators.required]),
    jornadaHoras: this.fb.control<number | null>(null, [Validators.required])
    // BUG-015: control `chefeUsuarioId` removido junto com o campo UI (mesmo
    // motivo de DEBITO-01 / BUG-012). Vinculo GESTOR→GESTOR e feito pelo
    // organograma apos criacao.
  });

  constructor(
    private fb: FormBuilder,
    private api: ColaboradoresApiService,
    private cargosApi: CargosApiService,
    private departamentosApi: DepartamentosApiService,
    private hierarquiaApi: HierarquiaApiService,
    private timesApi: TimesApiService,
    private message: MessageService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    const visibleChanged = changes['visible']?.currentValue === true;
    const colaboradorChanged = !!changes['colaborador'];

    if (visibleChanged) {
      this.prepararFormulario();
      if (!this.lookupsLoaded) {
        this.carregarLookups();
      }
      return;
    }

    if (colaboradorChanged && this.visible) {
      this.prepararFormulario();
      if (!this.lookupsLoaded) {
        this.carregarLookups();
      }
      // Se lookups já estavam carregados, prepararFormulario já disparou
      // carregarGestores/carregarTimes para o departamento resultante.
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isEdicao(): boolean {
    return !!this.colaborador?.id;
  }

  get lookupCarregando(): boolean {
    return this.cargosCarregando || this.departamentosCarregando || this.gestoresCarregando || this.timesCarregando;
  }

  onClose(): void {
    this.close.emit();
  }

  // ── Stepper methods ─────────────────────────────────────────────────────────

  canAdvance(): boolean {
    if (this.currentStep === 1) {
      const { nome, sobrenome, identificador, email } = this.form.controls;
      return nome.valid && sobrenome.valid && identificador.valid && email.valid;
    }
    if (this.currentStep === 2) {
      const { departamentoId, cargo, gestorId, timesIds, jornadaHoras } = this.form.controls;
      const gestorValid = this.perfil === 'GESTOR' ? true : gestorId.valid;
      const timesValid = this.perfil === 'GESTOR' ? true : timesIds.valid;
      return departamentoId.valid && cargo.valid && gestorValid && timesValid && jornadaHoras.valid;
    }
    return true;
  }

  nextStep(): void {
    if (this.currentStep === 1) {
      const { nome, sobrenome, identificador, email } = this.form.controls;
      nome.markAsTouched();
      sobrenome.markAsTouched();
      identificador.markAsTouched();
      email.markAsTouched();
      if (!this.canAdvance()) return;
    } else if (this.currentStep === 2) {
      const { departamentoId, cargo, gestorId, timesIds, jornadaHoras } = this.form.controls;
      departamentoId.markAsTouched();
      cargo.markAsTouched();
      if (this.perfil !== 'GESTOR') {
        gestorId.markAsTouched();
        timesIds.markAsTouched();
      }
      jornadaHoras.markAsTouched();
      if (!this.canAdvance()) return;
    }
    if (this.currentStep < this.totalSteps) {
      this.currentStep++;
    }
  }

  prevStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  submit(): void {
    // Para GESTOR, gestorId e timesIds são opcionais — desabilita para excluir da validação do form
    if (this.perfil === 'GESTOR') {
      this.form.controls.gestorId.disable({ emitEvent: false });
      this.form.controls.timesIds.disable({ emitEvent: false });
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const isEdicao = this.isEdicao && !!this.colaborador;

    if (isEdicao) {
      const payload = this.buildUpdatePayload();

      this.loading = true;
      this.api.atualizar(this.colaborador!.id, payload, this.avatarFile ?? undefined)
        .pipe(finalize(() => (this.loading = false)))
        .subscribe({
          next: () => {
            if (this.showSuccessToast) {
              this.message.add({ severity: 'success', summary: 'Sucesso', detail: 'Colaborador atualizado' });
            }
            this.saved.emit();
          },
          error: (error) => {
            // BUG-013: helper cascateia mensagem (PT Manager) → message (EN Java) → root → fallback.
            const detail = extractManagerErrorMessage(error, 'Falha ao salvar colaborador.');
            this.message.add({ severity: 'error', summary: 'Erro', detail });
          }
        });
      return;
    }

    if (this.perfil === 'GESTOR') {
      this.submitGestor();
      return;
    }

    const payload = this.buildCreatePayload();
    if (!payload.identificador) {
      this.form.controls.identificador.setErrors({ required: true });
      return;
    }
    if (!Number.isFinite(payload.gestorId) || !Number.isFinite(payload.departamentoId)) {
      if (!Number.isFinite(payload.gestorId)) this.form.controls.gestorId.setErrors({ required: true });
      if (!Number.isFinite(payload.departamentoId)) this.form.controls.departamentoId.setErrors({ required: true });
      this.form.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.api.criar(payload, this.avatarFile ?? undefined)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: () => {
          if (this.showSuccessToast) {
            this.message.add({ severity: 'success', summary: 'Sucesso', detail: 'Colaborador criado' });
          }
          this.saved.emit();
        },
        error: (error) => {
          const status = error?.status;
          if (status === 409) {
            this.message.add({ severity: 'warn', summary: 'Duplicado', detail: 'Já existe colaborador com este identificador.' });
            return;
          }
          if (status === 400) {
            this.message.add({ severity: 'warn', summary: 'Dados inválidos', detail: 'Identificador único é obrigatório.' });
            return;
          }
          // BUG-013: helper cascateia mensagem (PT Manager) → message (EN Java) → root → fallback.
          const detail = extractManagerErrorMessage(error, 'Falha ao salvar colaborador.');
          this.message.add({ severity: 'error', summary: 'Erro', detail });
        }
      });
  }

  private submitGestor(): void {
    const raw = this.form.getRawValue();

    if (!raw.identificador?.trim()) {
      this.form.controls.identificador.setErrors({ required: true });
      return;
    }
    if (!raw.departamentoId) {
      this.form.controls.departamentoId.setErrors({ required: true });
      this.form.markAllAsTouched();
      return;
    }

    const payload: CriarUsuarioPortalRequest = {
      identificador: (raw.identificador || '').trim(),
      nome: (raw.nome || '').trim(),
      sobrenome: (raw.sobrenome || '').trim(),
      email: (raw.email || '').trim(),
      emailPessoal: raw.emailPessoal?.trim() || undefined,
      telefone: raw.telefone?.trim() || undefined,
      cargo: raw.cargo?.trim() || undefined,
      jornadaHoras: raw.jornadaHoras ?? undefined,
      dataContratacao: raw.dataContratacao || undefined,
      dataNascimento: raw.dataNascimento || undefined,
      // BUG-015 (regressivo-pos-migracao 2026-07-29): incluir genero/estadoCivil
      // no payload de criacao de GESTOR. O form ja capturava esses valores no
      // Step 3, mas este build ignorava — sintoma do bug do 3o modal "Novo Gestor"
      // (SHARED com perfil=GESTOR). Padrao "omit-when-null" — nao envia quando
      // usuario nao selecionou. Backend Node (CriarUsuarioInputDto) ja aceita.
      genero: (raw.genero as Genero | null) ?? undefined,
      estadoCivil: (raw.estadoCivil as EstadoCivil | null) ?? undefined,
      perfil: 'GESTOR',
      departamentosIds: raw.departamentoId ? [Number(raw.departamentoId)] : [],
      responsaveisIds: raw.gestorId ? [Number(raw.gestorId)] : [],
      timesIds: raw.timesIds?.length ? (raw.timesIds as number[]) : undefined
      // BUG-015: `chefeUsuarioId` removido junto com o campo UI (mesmo motivo
      // de DEBITO-01 / BUG-012). Vinculo via organograma.
    };

    this.executarCriarGestor(payload);
  }

  /**
   * BUG-017: chamada de criacao isolada num helper reutilizavel. O primeiro
   * submit vai sem `forcarTransferencia`; se der 409 estruturado
   * (`TIMES_JA_ATRIBUIDOS`), abrimos o modal de confirmacao e — se o admin
   * clicar "Sim, transferir" — re-chamamos esta mesma funcao com o payload
   * enriquecido com a flag.
   */
  private executarCriarGestor(payload: CriarUsuarioPortalRequest): void {
    this.loading = true;
    this.hierarquiaApi.criarUsuarioPortal(payload)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res) => {
          if (res.falhas > 0) {
            this.message.add({
              severity: 'error',
              summary: 'Erro',
              detail: res.erros?.[0] || 'Não foi possível criar o gestor.'
            });
            return;
          }
          if (this.showSuccessToast) {
            this.message.add({ severity: 'success', summary: 'Sucesso', detail: 'Gestor criado com sucesso' });
          }
          this.saved.emit();
        },
        error: (error) => {
          // BUG-017: primeiro checa se e o 409 estruturado de conflito de
          // transferencia — se for, abre modal em vez de mostrar toast de erro.
          const conflitos = extractTimesJaAtribuidosConflitos(error);
          if (conflitos) {
            this.abrirDialogTransferenciaCriarGestor(payload, conflitos);
            return;
          }

          const status = error?.status;
          if (status === 409) {
            this.message.add({ severity: 'warn', summary: 'Duplicado', detail: 'Já existe um usuário com este identificador.' });
            return;
          }
          // BUG-013: helper cascateia mensagem (PT Manager) → message (EN Java) → root → fallback.
          const detail = extractManagerErrorMessage(error, 'Falha ao criar gestor.');
          this.message.add({ severity: 'error', summary: 'Erro', detail });
        }
      });
  }

  /** BUG-017: abre modal de confirmacao guardando o payload original. */
  private abrirDialogTransferenciaCriarGestor(
    payload: CriarUsuarioPortalRequest,
    conflitos: TimeConflito[]
  ): void {
    this.payloadGestorEmEspera = payload;
    this.conflitosTransferencia = conflitos;
    this.transferenciaDialogVisivel = true;
  }

  /**
   * BUG-017: handler do `(confirmar)` do modal. Re-executa a criacao com
   * `forcarTransferencia: true`. NAO fechamos o dialog aqui — o dialog
   * mostra `loading=true` durante a re-submissao (padrao UX: usuario ve
   * que a acao esta acontecendo). Fechamento acontece no `next`/`error`.
   */
  onConfirmarTransferenciaCriarGestor(): void {
    const payload = this.payloadGestorEmEspera;
    if (!payload) return;

    const payloadForcado: CriarUsuarioPortalRequest = {
      ...payload,
      forcarTransferencia: true
    };

    this.transferenciaLoading = true;
    this.hierarquiaApi.criarUsuarioPortal(payloadForcado)
      .pipe(finalize(() => (this.transferenciaLoading = false)))
      .subscribe({
        next: (res) => {
          this.fecharDialogTransferencia();
          if (res.falhas > 0) {
            this.message.add({
              severity: 'error',
              summary: 'Erro',
              detail: res.erros?.[0] || 'Não foi possível criar o gestor.'
            });
            return;
          }
          if (this.showSuccessToast) {
            this.message.add({ severity: 'success', summary: 'Sucesso', detail: 'Gestor criado e times transferidos com sucesso' });
          }
          this.saved.emit();
        },
        error: (error) => {
          this.fecharDialogTransferencia();
          const detail = extractManagerErrorMessage(error, 'Falha ao transferir times.');
          this.message.add({ severity: 'error', summary: 'Erro', detail });
        }
      });
  }

  /** BUG-017: handler do `(cancelar)` do modal. Descarta payload em espera. */
  onCancelarTransferenciaCriarGestor(): void {
    this.fecharDialogTransferencia();
  }

  private fecharDialogTransferencia(): void {
    this.transferenciaDialogVisivel = false;
    this.transferenciaLoading = false;
    this.conflitosTransferencia = [];
    this.payloadGestorEmEspera = null;
  }

  criarDepartamentoRapido(): void {
    if (!this.novoDepartamentoNome?.trim()) return;
    this.criandoDepartamento = true;
    this.departamentosApi.criar(this.novoDepartamentoNome.trim()).subscribe({
      next: (created) => {
        const novaOpcao = { label: created.nome, value: created.id };
        this.departamentosOptions = [...this.departamentosOptions, novaOpcao];
        this.form.controls.departamentoId.setValue(created.id);
        this.quickCreateDepartamento = false;
        this.novoDepartamentoNome = '';
        this.criandoDepartamento = false;
        this.message.add({ severity: 'success', summary: 'Sucesso', detail: `Departamento "${created.nome}" criado.` });
      },
      error: () => {
        this.criandoDepartamento = false;
        this.message.add({ severity: 'error', summary: 'Erro', detail: 'Não foi possível criar o departamento.' });
      }
    });
  }

  criarCargoRapido(): void {
    if (!this.novoCargoNome?.trim()) return;
    this.criandoCargo = true;
    this.cargosApi.criar(this.novoCargoNome.trim()).subscribe({
      next: (created) => {
        const novaOpcao = { label: created.nome, value: created.nome };
        this.cargosOptions = [...this.cargosOptions, novaOpcao];
        this.form.controls.cargo.setValue(created.nome);
        this.quickCreateCargo = false;
        this.novoCargoNome = '';
        this.criandoCargo = false;
        this.message.add({ severity: 'success', summary: 'Sucesso', detail: `Cargo "${created.nome}" criado.` });
      },
      error: () => {
        this.criandoCargo = false;
        this.message.add({ severity: 'error', summary: 'Erro', detail: 'Não foi possível criar o cargo.' });
      }
    });
  }

  criarTimeRapido(): void {
    if (!this.novoTimeNome?.trim()) return;
    const departamentoId = this.form.controls.departamentoId.value;
    if (!departamentoId) return;
    this.criandoTime = true;
    this.timesApi.criar({ nome: this.novoTimeNome.trim(), departamentoId }).subscribe({
      next: (created) => {
        this.timesOptions = [...this.timesOptions, created];
        const atual: number[] = this.form.controls.timesIds.value ?? [];
        this.form.controls.timesIds.setValue([...atual, created.id]);
        this.quickCreateTime = false;
        this.novoTimeNome = '';
        this.criandoTime = false;
        this.message.add({ severity: 'success', summary: 'Sucesso', detail: `Time "${created.nome}" criado.` });
      },
      error: () => {
        this.criandoTime = false;
        this.message.add({ severity: 'error', summary: 'Erro', detail: 'Não foi possível criar o time.' });
      }
    });
  }

  private pendingGestorId: number | null = null;

  private prepararFormulario(): void {
    if (!this.visible) return;

    this.currentStep = 1;

    const colab = this.colaborador;
    this.pendingGestorId = colab?.gestor?.id ?? null;
    this.avatarFile = null;
    this.avatarPreviewUrl = colab?.avatarUrl ?? null;

    const departamentoId = colab?.departamentoId ?? colab?.departamento?.id ?? null;
    const timesIds = colab?.times?.map((t) => t.id) ?? null;

    this.form.reset(
      {
        identificador: colab?.identificador ?? '',
        nome: colab?.nome ?? '',
        sobrenome: colab?.sobrenome ?? '',
        email: colab?.email ?? '',
        telefone: colab?.telefone ?? '',
        cargo: colab?.cargo ?? '',
        gestorId: null,
        departamentoId,
        emailPessoal: colab?.emailPessoal ?? '',
        estadoCivil: (colab?.estadoCivil as EstadoCivil | null) ?? null,
        dataContratacao: colab?.dataContratacao ?? null,
        genero: (colab?.genero as Genero | null) ?? null,
        dataNascimento: colab?.dataNascimento ?? null,
        timesIds,
        jornadaHoras: colab?.jornadaHoras ?? null
      },
      { emitEvent: false }
    );

    // Sincroniza os p-calendars
    const dnVal = colab?.dataNascimento;
    if (dnVal) {
      const [y, m, d] = dnVal.split('-').map(Number);
      this.dataNascimentoCalendar = new Date(y, m - 1, d);
    } else {
      this.dataNascimentoCalendar = null;
    }

    const dcVal = colab?.dataContratacao;
    if (dcVal) {
      const [y, m, d] = dcVal.split('-').map(Number);
      this.dataContratacaoCalendar = new Date(y, m - 1, d);
    } else {
      this.dataContratacaoCalendar = null;
    }

    if (colab) {
      this.isInitialLoad = true;
      this.form.controls.identificador.disable({ emitEvent: false });
    } else {
      this.form.controls.identificador.enable({ emitEvent: false });
    }

    // Para GESTOR, gestorId e timesIds são opcionais
    if (this.perfil === 'GESTOR') {
      this.form.controls.gestorId.clearValidators();
      this.form.controls.gestorId.updateValueAndValidity({ emitEvent: false });
      this.form.controls.timesIds.clearValidators();
      this.form.controls.timesIds.updateValueAndValidity({ emitEvent: false });
    }

    // Estado limpo entre aberturas: options dependentes de departamento não podem
    // vazar da sessão anterior. Sem isso, o multiSelect de Times poderia mostrar
    // opções obsoletas até a próxima resposta do backend.
    this.gestorOptions = [];
    this.timesOptions = [];

    // PR-4: aplicar prefill contextual do organograma (departamento, time, gestor pai).
    // Só aplicado quando não há colaborador em edição (criação) e os valores foram fornecidos.
    if (!colab && this.prefillDepartamentoId) {
      this.form.controls.departamentoId.setValue(this.prefillDepartamentoId, { emitEvent: false });
    }

    // PR-4: pré-definir pendingGestorId ANTES de carregarGestores para que o
    // match dentro do subscribe capture o valor correto mesmo em mocks síncronos.
    if (!colab && this.prefillGestorPaiId) {
      this.pendingGestorId = this.prefillGestorPaiId;
    }

    // Bloqueia campos dependentes de departamento
    const depAtual = this.form.controls.departamentoId.value;
    if (!depAtual) {
      this.form.controls.gestorId.disable({ emitEvent: false });
      this.form.controls.timesIds.disable({ emitEvent: false });
    } else {
      // Reabertura em edição (ou reabertura já com departamento pré-preenchido):
      // dispara diretamente o carregamento, sem depender do valueChanges — o
      // reset do form usa emitEvent:false, então o observable não propaga sozinho.
      this.carregarGestores(Number(depAtual), true);
      this.carregarTimes(Number(depAtual));
    }
  }

  get dataNascimentoDate(): Date | null {
    return this.dataNascimentoCalendar;
  }

  onDataNascimentoSelect(date: Date): void {
    if (!date) return;
    this.dataNascimentoCalendar = date;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    this.form.controls.dataNascimento.setValue(`${y}-${m}-${d}`);
  }

  onDataContratacaoSelect(date: Date): void {
    if (!date) return;
    this.dataContratacaoCalendar = date;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    this.form.controls.dataContratacao.setValue(`${y}-${m}-${d}`);
  }

  onAvatarSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }
    if (file.size > this.maxAvatarSizeBytes) {
      this.message.add({
        severity: 'warn',
        summary: 'Imagem muito grande',
        detail: 'Tamanho máximo permitido é 2MB.'
      });
      input.value = '';
      this.avatarFile = null;
      this.avatarPreviewUrl = this.colaborador?.avatarUrl ?? null;
      return;
    }
    if (!this.isAvatarFileValid(file)) {
      this.message.add({
        severity: 'warn',
        summary: 'Formato inválido',
        detail: 'Use uma imagem JPG ou PNG.'
      });
      input.value = '';
      return;
    }
    this.avatarFile = file;
    this.avatarPreviewUrl = URL.createObjectURL(file);
  }

  private isAvatarFileValid(file: File): boolean {
    const mimeOk = this.allowedAvatarMimeTypes.includes(file.type);
    const lower = file.name.toLowerCase();
    const extOk = this.allowedAvatarExtensions.some((ext) => lower.endsWith(ext));
    return mimeOk || extOk;
  }

  private carregarLookups(): void {
    this.lookupsLoaded = true;

    // Subscribe no valueChanges ANTES de carregar dados — garante que mudanças
    // de departamento (inclusive a inicial via prepararFormulario) sejam capturadas.
    // Observação: sem distinctUntilChanged aqui. Ele filtrava disparos legítimos
    // após reabertura do modal (form.reset com emitEvent:false não propaga null
    // para o observable, e uma re-seleção do MESMO departamento ficava presa).
    // Todos os pontos que setam departamentoId sem intenção de recarregar já
    // usam `{ emitEvent: false }`.
    this.form.controls.departamentoId.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((id) => {
        if (id && id > 0) {
          const initialLoad = this.isInitialLoad;
          this.isInitialLoad = false;

          if (!initialLoad) {
            // Troca de departamento pelo usuário: zera campos dependentes
            this.form.controls.timesIds.setValue(null, { emitEvent: false });
            this.form.controls.gestorId.setValue(null, { emitEvent: false });
          }

          this.carregarGestores(Number(id), initialLoad);
          this.carregarTimes(Number(id));
        } else {
          this.gestorOptions = [];
          this.form.controls.gestorId.setValue(null);
          this.form.controls.gestorId.disable({ emitEvent: false });
          this.timesOptions = [];
          this.form.controls.timesIds.setValue(null, { emitEvent: false });
          this.form.controls.timesIds.disable({ emitEvent: false });
        }
      });

    this.cargosCarregando = true;
    this.cargosApi.listar(0, 200).subscribe({
      next: (res) => {
        const lista = res.content ?? [];
        this.cargosOptions = lista
          .filter((c) => c.status === 'ATIVO')
          .map((c) => ({ label: c.nome, value: c.nome }));
      },
      error: () => {
        this.message.add({ severity: 'warn', summary: 'Aviso', detail: 'Não foi possível carregar cargos.' });
      },
      complete: () => {
        this.cargosCarregando = false;
      }
    });

    this.departamentosCarregando = true;
    this.departamentosApi.listarAtivos().subscribe({
      next: (lista) => {
        this.departamentosOptions = (lista ?? []).map((d) => ({ label: d.nome, value: d.id }));

        // Re-set departamentoId para o PrimeNG dropdown casar o valor contra as options recém-populadas.
        // `emitEvent: false` — o disparo inicial de gestores/times já é feito por prepararFormulario.
        const depAtual = this.form.controls.departamentoId.value;
        if (depAtual && depAtual > 0) {
          this.form.controls.departamentoId.setValue(depAtual, { emitEvent: false });
        }
      },
      error: () => {
        this.message.add({ severity: 'warn', summary: 'Aviso', detail: 'Não foi possível carregar departamentos.' });
      },
      complete: () => {
        this.departamentosCarregando = false;
      }
    });

    // O disparo inicial de gestores/times para edição é feito por prepararFormulario,
    // que roda em toda reabertura — não apenas na primeira carga (lookupsLoaded).
  }

  private carregarGestores(depId: number, manterValor: boolean): void {
    this.gestoresCarregando = true;
    this.gestorOptions = [];
    this.form.controls.gestorId.disable({ emitEvent: false });
    this.hierarquiaApi.listarGestores(depId).subscribe({
      next: (gestores) => {
        this.gestorOptions = gestores.map(g => ({
          entidadeId: g.entidadeId,
          tipo: 'GESTOR' as any,
          idOrigem: g.idOrigem,
          nome: g.nome,
          identificador: '',
          status: 'ATIVO' as any,
          departamentos: []
        }));
        this.gestoresCarregando = false;
        this.form.controls.gestorId.enable({ emitEvent: false });

        if (manterValor && this.pendingGestorId) {
          const match = this.gestorOptions.find(
            (g) =>
              g.idOrigem === this.pendingGestorId ||
              g.entidadeId === this.pendingGestorId
          );
          const usuarioId = match?.idOrigem ?? null;
          this.pendingGestorId = null;
          setTimeout(() => {
            this.form.controls.gestorId.setValue(usuarioId, { emitEvent: false });
          }, 0);
        } else if (!manterValor) {
          this.form.controls.gestorId.setValue(null);
        }
      },
      error: () => {
        this.message.add({ severity: 'warn', summary: 'Aviso', detail: 'Não foi possível carregar gestores do departamento.' });
        this.gestoresCarregando = false;
      },
      complete: () => {
        this.gestoresCarregando = false;
        this.form.controls.gestorId.enable({ emitEvent: false });
      }
    });
  }

  private carregarTimes(depId: number): void {
    this.timesCarregando = true;
    this.form.controls.timesIds.disable({ emitEvent: false });
    this.timesApi.listar(0, 200, undefined, depId).pipe(finalize(() => {
      this.timesCarregando = false;
      this.form.controls.timesIds.enable({ emitEvent: false });
    })).subscribe({
      next: (res) => {
        this.timesOptions = res.content ?? [];

        // PR-4: pré-selecionar time contextual do organograma se prefill fornecido
        // e não há colaborador em edição (criação com contexto de time).
        if (!this.colaborador && this.prefillTimeId) {
          const timeExiste = this.timesOptions.some((t) => t.id === this.prefillTimeId);
          if (timeExiste) {
            this.form.controls.timesIds.setValue([this.prefillTimeId!], { emitEvent: false });
          }
        }
      },
      error: () => {
        this.timesOptions = [];
      }
    });
  }

  private buildCreatePayload(): ColaboradorPayload {
    const raw = this.form.getRawValue();
    return {
      identificador: (raw.identificador || '').trim(),
      nome: (raw.nome || '').trim(),
      sobrenome: (raw.sobrenome || '').trim(),
      cargo: (raw.cargo || '').trim(),
      gestorId: this.toNumberRequired(raw.gestorId),
      departamentoId: this.toNumberRequired(raw.departamentoId),
      email: (raw.email || '').trim(),
      emailPessoal: raw.emailPessoal?.trim() || undefined,
      telefone: raw.telefone?.trim() || undefined,
      estadoCivil: raw.estadoCivil ?? undefined,
      dataContratacao: raw.dataContratacao || undefined,
      genero: raw.genero ?? undefined,
      dataNascimento: raw.dataNascimento || undefined,
      timesIds: raw.timesIds ?? undefined,
      jornadaHoras: raw.jornadaHoras ?? undefined
    };
  }

  private buildUpdatePayload(): ColaboradorAtualizacaoPayload {
    const raw = this.form.getRawValue();
    return {
      nome: raw.nome?.trim() || undefined,
      sobrenome: raw.sobrenome?.trim() || undefined,
      cargo: raw.cargo?.trim() || undefined,
      gestorId: this.toNumberOptional(raw.gestorId),
      departamentoId: this.toNumberOptional(raw.departamentoId),
      email: raw.email?.trim() || undefined,
      emailPessoal: raw.emailPessoal?.trim() || undefined,
      telefone: raw.telefone?.trim() || undefined,
      estadoCivil: raw.estadoCivil ?? undefined,
      dataContratacao: raw.dataContratacao || undefined,
      genero: raw.genero ?? undefined,
      dataNascimento: raw.dataNascimento || undefined,
      timesIds: raw.timesIds ?? undefined,
      jornadaHoras: raw.jornadaHoras ?? undefined
    };
  }

  private toNumberRequired(value: any): number {
    return Number(value);
  }

  private toNumberOptional(value: any): number | undefined {
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
  }
}
